import { API } from "aws-amplify";

export interface CustomerCallData {
  id: string;
  status: string;
  customerId: string;
  serviceId?: string;
  modelId: string;
  tokenCountPrompt: string;
  tokenCountResponse: string;
  createdAt: string;
  country: string;
  region: string;
  city: string;
  agentId?: string;
  customerUserId?: string;
  customerUserName?: string;
  customerUserEmail?: string;
  // Additional fields from API response (usage fields)
  customer_user_id?: string;
  customer_user_name?: string;
  customer_user_email?: string;
}

export interface CustomerCallsResponse {
  data: CustomerCallData[];
  hasMore?: boolean;
  nextOffset?: number;
}

export interface FetchCustomerCallsParams {
  projectId?: string; // Optional when using JWT authentication
  customerId: string;
  apiKey?: string;
  jwtToken?: string;
  createdAfter?: string;
  fields?: string;
  offset?: number;
  limit?: number;
}

/**
 * Fetches customer calls data from the LLM Public API
 * Uses the /customers/{id}/calls endpoint with paging support
 * 
 * @param params - Object containing all required parameters
 * @returns Promise with customer calls data
 */
export async function fetchCustomerCalls(
  params: FetchCustomerCallsParams
): Promise<CustomerCallsResponse> {
  
  const {
    projectId = "", // Default to empty string for JWT auth
    customerId,
    apiKey,
    jwtToken,
    createdAfter,
    fields = "usage", // Default to usage fields for performance
    offset = 0,
    limit = 100
  } = params;

  // URL encode the customer ID to handle email addresses and special characters
  // When using JWT authentication, projectId is extracted from the token by the backend
  // so we don't need to remove it from the customerId
  let cleanCustomerId = customerId;
  if (projectId && customerId.startsWith(projectId)) {
    // Remove projectId prefix if present and we have a projectId (API key auth)
    cleanCustomerId = customerId.replace(projectId, '');
  }
  const encodedCustomerId = encodeURIComponent(cleanCustomerId);

  // Build query parameters
  const queryParams = new URLSearchParams();
  
  if (fields) {
    queryParams.append('fields', fields);
  }
  
  if (createdAfter) {
    queryParams.append('createdAfter', createdAfter);
  }
  
  if (offset > 0) {
    queryParams.append('offset', offset.toString());
  }
  
  if (limit !== 100) {
    queryParams.append('limit', limit.toString());
  }

  const queryString = queryParams.toString();
  const endpoint = `/customers/${encodedCustomerId}/calls${queryString ? `?${queryString}` : ''}`;

  // Build headers based on authentication method
  const headers: Record<string, string> = {};
  
  if (jwtToken) {
    // Use JWT token authentication
    headers['Authorization'] = `Bearer ${jwtToken}`;
  } else if (apiKey && projectId) {
    // Use API key authentication (requires projectId)
    headers['x-api-key'] = apiKey;
    headers['x-project-id'] = projectId;
  } else {
    throw new Error('Either jwtToken or both apiKey and projectId must be provided');
  }
  
  try {
    const response = await API.get("llmpublicapi", endpoint, {
      headers,
    });

    // The API returns an array of calls
    const callsData = Array.isArray(response) ? response : [];
    
    return { 
      data: callsData,
      hasMore: callsData.length === limit, // If we got a full page, there might be more
      nextOffset: callsData.length > 0 ? offset + callsData.length : undefined
    };
  } catch (error) {
    console.error("Error fetching customer calls:", error);
    throw error;
  }
}

/**
 * Fetches customer calls using the customer's lastReplenishedAt date as the createdAfter filter
 * This is a convenience wrapper for the common use case of showing calls since last replenishment
 */
export async function fetchCustomerCallsSinceReplenishment(
  projectId: string | undefined,
  customerId: string,
  apiKey?: string,
  jwtToken?: string,
  lastReplenishedAt?: string,
  offset: number = 0,
  limit: number = 100
): Promise<CustomerCallsResponse> {
  
  return fetchCustomerCalls({
    projectId: projectId || "", // Allow empty projectId for JWT auth
    customerId,
    apiKey,
    jwtToken,
    createdAfter: lastReplenishedAt,
    fields: "usage", // Use usage fields as requested
    offset,
    limit
  });
}

/**
 * Fetches all customer calls (paginated) since a specific date
 * This function handles automatic pagination to get all results
 */
export async function fetchAllCustomerCallsSinceDate(
  projectId: string | undefined,
  customerId: string,
  apiKey?: string,
  jwtToken?: string,
  createdAfter?: string,
  maxCalls: number = 1000
): Promise<CustomerCallData[]> {
  
  const allCalls: CustomerCallData[] = [];
  let offset = 0;
  const limit = 100;
  let hasMore = true;
  
  while (hasMore && allCalls.length < maxCalls) {
    try {
      const response = await fetchCustomerCalls({
        projectId: projectId || "", // Allow empty for JWT auth
        customerId,
        apiKey,
        jwtToken,
        createdAfter,
        fields: "usage",
        offset,
        limit
      });
      
      allCalls.push(...response.data);
      
      // Check if we should continue paging
      hasMore = response.hasMore === true && response.data.length === limit;
      offset = response.nextOffset || (offset + response.data.length);
      
      // Safety check to avoid infinite loops
      if (response.data.length === 0) {
        hasMore = false;
      }
      
    } catch (error) {
      console.error("Error in pagination:", error);
      break;
    }
  }
  
  return allCalls;
}
